import { Injectable } from '@angular/core';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import produce from 'immer';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PersistentStorageService, InitStoreFacade, MeasurementQcDBFacade } from '@monorepo/core-data';
import { getMeasurementQcInitialState } from './measurement-qc.reducer';
import {
  InitializeMeasurementQcDB,
  MeasurementQcDBActionTypes,
  IncreasePiecesMeasuredCountbySizeAndBctxKey,
  MeasurementQcNoAction,
  SetMeasurementQcDB,
  UpdateBctxMap,
} from './measurement-qc.actions';
import { TranslateService } from '@ngx-translate/core';
import { InputAppAPIHelperUtil } from '@monorepo/utils';

@Injectable({
  providedIn: 'root',
})
export class MeasurementQcDBEffects {
  constructor(
    private action$: Actions,
    private initStoreFacade: InitStoreFacade,
    private persistentStorageService: PersistentStorageService,
    private apiHelper: InputAppAPIHelperUtil,
    private mqcDBFacade: MeasurementQcDBFacade,
    private translate: TranslateService,
  ) {}

  InitializeMeasurementQcDB$ = createEffect(() =>
    this.action$.pipe(
      ofType<InitializeMeasurementQcDB>(MeasurementQcDBActionTypes.InitializeMeasurementQcDB),
      switchMap((action) => {
        return this.initStoreFacade.getNextCacheResetTimestamp().pipe(
          take(1),
          map((nextCacheRestTimestamp) => [action, nextCacheRestTimestamp] as const),
        );
      }),
      switchMap(([action, nextCacheRestTimestamp]) =>
        from(this.persistentStorageService.get('[APP][MEASUREMENT_QC_DB]')).pipe(
          take(1),
          switchMap((mqcDb) => {
            const mqcDBInitialState = getMeasurementQcInitialState(nextCacheRestTimestamp);
            if (mqcDb) {
              if (mqcDb.nextCacheResetTimestamp === nextCacheRestTimestamp)
                return of(new SetMeasurementQcDB(mqcDb, true));
              else {
                // currently we are flushing out the existing db and setting initial state at cache reset
                return from(this.persistentStorageService.set('[APP][MEASUREMENT_QC_DB]', mqcDBInitialState)).pipe(
                  take(1),
                  map(() => {
                    return new SetMeasurementQcDB(mqcDBInitialState, true);
                  }),
                );
              }
            } else {
              return from(this.persistentStorageService.set('[APP][MEASUREMENT_QC_DB]', mqcDBInitialState)).pipe(
                take(1),
                map(() => {
                  return new SetMeasurementQcDB(mqcDBInitialState, true);
                }),
              );
            }
          }),
        ),
      ),
    ),
  );

  MeasurementQcDBInitCompleted$ = createEffect(
    () =>
      this.action$.pipe(
        ofType<SetMeasurementQcDB>(MeasurementQcDBActionTypes.SetMeasurementQcDB),
        tap((action) => {
          if (action.init) {
            this.mqcDBFacade.measurementQcDBInitCompleted();
          }
        }),
      ),
    { dispatch: false },
  );

  UpdateBctxMap$ = createEffect(() =>
    this.action$.pipe(
      ofType<UpdateBctxMap>(MeasurementQcDBActionTypes.UpdateBctxMap),
      switchMap((action) =>
        from(this.persistentStorageService.get('[APP][MEASUREMENT_QC_DB]')).pipe(
          take(1),
          switchMap((mqcDb) => {
            if (!mqcDb) {
              this.apiHelper.showPopup(
                this.translate.instant('mqc_app.common.error'),
                this.translate.instant('mqc_app.common.something_went_wrong'),
              );
              return of(new MeasurementQcNoAction());
            }
            const { bctxKey, updatedData, isThisLastFetchedTag } = action;
            const updatedMqcDb = produce(mqcDb, (draft) => {
              if (isThisLastFetchedTag) {
                draft.lastFetchedTagData = {
                  perPieceTagDisplayId: updatedData.currentScannedTagDetails.perPieceTagDisplayId,
                  bctxKey: bctxKey,
                  size: updatedData.currentScannedTagDetails.size,
                };
              }
              draft.bctxMap[bctxKey] = updatedData;
            });
            return from(this.persistentStorageService.set('[APP][MEASUREMENT_QC_DB]', updatedMqcDb)).pipe(
              take(1),
              map(() => new SetMeasurementQcDB(updatedMqcDb)),
            );
          }),
        ),
      ),
    ),
  );

  IncreasePiecesMeasuredCountbySizeAndBctxKey$ = createEffect(() =>
    this.action$.pipe(
      ofType<IncreasePiecesMeasuredCountbySizeAndBctxKey>(
        MeasurementQcDBActionTypes.IncreasePiecesMeasuredCountbySizeAndBctxKey,
      ),
      switchMap((action) =>
        from(this.persistentStorageService.get('[APP][MEASUREMENT_QC_DB]')).pipe(
          take(1),
          switchMap((mqcDb) => {
            if (!mqcDb) {
              this.apiHelper.showPopup(
                this.translate.instant('mqc_app.common.error'),
                this.translate.instant('mqc_app.common.something_went_wrong'),
              );
              return of(new MeasurementQcNoAction());
            }
            const updatedMqcDb = produce(mqcDb, (draft) => {
              const { bctxKey, tagSize } = action;
              const bctxItem = draft.bctxMap[bctxKey];
              draft.lastMeasuredTagData = {
                perPieceTagDisplayId: bctxItem.currentScannedTagDetails.perPieceTagDisplayId,
                bctxKey: bctxKey,
                size: tagSize,
              };
              const sizeItem = bctxItem.sizesMeasurementQC.find((size) => size.size === tagSize);
              if (sizeItem) {
                sizeItem.piecesMeasured += 1;
              }
            });
            return from(this.persistentStorageService.set('[APP][MEASUREMENT_QC_DB]', updatedMqcDb)).pipe(
              take(1),
              map(() => new SetMeasurementQcDB(updatedMqcDb)),
            );
          }),
        ),
      ),
    ),
  );
}
